import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, XAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Payment, EarningsResponse, SpendingsResponse, AssignmentItem } from "@/schemas/payment";

type PaymentSummaryResponse = EarningsResponse | SpendingsResponse;

export function EarningsMainPanel({
  payments,
  summary,
  assignments = [],
}: {
  payments: Array<Payment>;
  summary: PaymentSummaryResponse;
  assignments?: Array<AssignmentItem>;
}) {
  const { t } = useTranslation("payments");
  const chartConfig = {
    earnings: {
      label: t("earnings"),
      color: "var(--chart-1)",
    },
  };
  const [tab, setTab] = useState<"weekly" | "monthly" | "annual">("weekly");
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());

  // Prepare chart data from payments based on tab
  const prepareChartData = () => {
    let filtered = payments;
    if (tab === "weekly") {
      filtered = payments.filter((p) => {
        const d = new Date(p.createdAt);
        return d.getMonth() === month && d.getFullYear() === year;
      });
    } else if (tab === "monthly") {
      filtered = payments.filter((p) => {
        const d = new Date(p.createdAt);
        return d.getFullYear() === year;
      });
    } // for annual, all

    const grouped: Record<string, { earned: number; pending: number }> = {};
    filtered.forEach((p) => {
      const d = new Date(p.createdAt);
      let key: string;
      if (tab === "weekly") {
        const week = Math.ceil(d.getDate() / 7);
        key = `Week ${week}`;
      } else if (tab === "monthly") {
        key = d.toLocaleDateString(undefined, { month: "short" });
      } else {
        // annual
        key = d.getFullYear().toString();
      }
      if (!(key in grouped)) grouped[key] = { earned: 0, pending: 0 };
      if (p.payment_status === "completed") {
        grouped[key].earned += p.amount;
      } else {
        grouped[key].pending += p.amount;
      }
    });

    const chartData = Object.entries(grouped).map(
      ([date, { earned, pending }]) => ({
        date,
        earned,
        pending,
      }),
    );

    // Sort
    chartData.sort((a, b) => {
      if (tab === "weekly")
        return parseInt(a.date.split(" ")[1]) - parseInt(b.date.split(" ")[1]);
      if (tab === "monthly") {
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return months.indexOf(a.date) - months.indexOf(b.date);
      }
      return parseInt(a.date) - parseInt(b.date);
    });

    return chartData;
  };

  const chartData = prepareChartData();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-garnet">
            {t("totalEarnings")}
          </h2>
          <p className="text-sm text-garnet/70">
            {tab === "annual"
              ? year
              : `${
                  [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ][month]
                } ${year}`}
          </p>
        </div>
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as typeof tab)}
        >
          <TabsList className="bg-transparent">
            <TabsTrigger value="weekly">{t("tabs.weekly")}</TabsTrigger>
            <TabsTrigger value="monthly">{t("tabs.monthly")}</TabsTrigger>
            <TabsTrigger value="annual">{t("tabs.annual")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {/* Summary Cards */}
      <div className="flex gap-8 mb-4">
        <div className="flex-1 bg-gray-50 rounded-xl p-6 flex flex-col gap-1">
          <div className="text-xs text-garnet/70 font-medium">
            {t("completedJobs", {
              count: summary.payments.filter(
                (p) => p.payment_status === "completed",
              ).length,
            })}
          </div>
          <div className="text-2xl font-bold text-garnet">
            {typeof summary.total === "number"
              ? `$${summary.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : "$0.00"}
          </div>
        </div>
        <div className="flex-1 bg-gray-50 rounded-xl p-6 flex flex-col gap-1">
          <div className="text-xs text-garnet/70 font-medium">
            {t("inProgressAssignments", { count: assignments.length })}
          </div>
          <div className="text-2xl font-bold text-garnet">
            {`$${assignments
              .reduce((sum, a) => sum + (a.payable || 0), 0)
              .toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          </div>
        </div>
        <div className="flex-1 bg-gray-50 rounded-xl p-6 flex flex-col gap-1">
          <div className="text-xs text-garnet/70 font-medium">{t("pending")}</div>
          <div className="text-2xl font-bold text-garnet">
            {`$${summary.payments
              .filter((p) => p.payment_status === "pending")
              .reduce((sum, p) => sum + p.payable, 0)
              .toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          </div>
        </div>
        <div className="flex-1 bg-red-50 rounded-xl p-6 flex flex-col gap-1">
          <div className="text-xs text-red-600 font-medium">{t("due")}</div>
          <div className="text-2xl font-bold text-red-600">
            {`$${summary.due_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? '0.00'}`}
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="relative">
        <ChartContainer config={chartConfig} className="min-h-[220px] w-full">
          {chartData.length === 0 ? (
            <div className="text-center py-8 text-garnet/60">
              {t("noEarningsData")}
            </div>
          ) : (
            <BarChart accessibilityLayer data={chartData} barSize={32}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <ChartTooltip
                content={<ChartTooltipContent labelKey="earned" />}
              />
              <Legend
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="rect"
                formatter={(value) => (
                  <span style={{ color: "#545A63" }}>
                    {value === "earned"
                      ? t("chart.earned")
                      : t("chart.pending")}
                  </span>
                )}
              />
              <Bar dataKey="earned" stackId="a" fill="#F8E6C0" radius={6} />
              <Bar dataKey="pending" stackId="a" fill="#D9D9D9" radius={6} />
            </BarChart>
          )}
        </ChartContainer>
        {/* Navigation at bottom */}
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            className="rounded-full bg-gray-100 px-2 py-1"
            onClick={() => {
              if (tab === "annual") setYear((y) => y - 1);
              else if (month === 0) {
                setMonth(11);
                setYear((y) => y - 1);
              } else {
                setMonth((m) => m - 1);
              }
            }}
            aria-label={t("navigation.previous")}
          >
            &#8592;
          </button>
          <span className="font-semibold text-garnet">
            {tab === "annual"
              ? year
              : `${
                  [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ][month]
                } ${year}`}
          </span>
          <button
            className="rounded-full bg-gray-100 px-2 py-1"
            onClick={() => {
              if (tab === "annual") setYear((y) => y + 1);
              else if (month === 11) {
                setMonth(0);
                setYear((y) => y + 1);
              } else {
                setMonth((m) => m + 1);
              }
            }}
            aria-label={t("navigation.next")}
          >
            &#8594;
          </button>
        </div>
      </div>
    </div>
  );
}
